import { Injectable, inject } from '@angular/core'
import { environment } from '../../environments/environment'
import { IngredientCsvTemplate } from '../types/csvTemplates'
import { catchError, map, Observable } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { ErrorService } from './error.service'

@Injectable({
  providedIn: 'root',
})
export class IngredientCsvTemplatesService {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'ingredient-csv-templates'

  /**
   * Creates a csv template
   * @param {IngredientCsvTemplate} template The template data
   * @returns {Observable<void>}
   */
  create(template: IngredientCsvTemplate): Observable<void> {
    return this.http.post<void>(this.URL, template).pipe(
      map((res: void) => {
        return res
      }),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Gets all csv templates for a company
   * @returns {Observable<Array<IngredientCsvTemplate>>} The csv templates found
   */
  getAll(): Observable<Array<IngredientCsvTemplate>> {
    return this.http
      .get<Array<IngredientCsvTemplate>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Gets a csv template by id
   * @param {string} id The template id
   * @returns {Observable<IngredientCsvTemplate>} Result
   */
  getById(id: string): Observable<IngredientCsvTemplate> {
    return this.http
      .get<IngredientCsvTemplate>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to update a csv template
   * @param {string} id The template id
   * @param {IngredientCsvTemplate} updates The template updates
   * @returns {Observable<void>}
   */
  edit(id: string, updates: IngredientCsvTemplate): Observable<void> {
    return this.http
      .put<void>(this.URL + `/${id}`, updates)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to delete a csv template
   * @param {string} id The template id
   * @returns {Observable<void>}
   */
  delete(id: string): Observable<void> {
    return this.http
      .delete<void>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Downloads a csv template by id
   * @param {string} id The template id
   * @returns {Observable<string>} Base64 result
   */
  download(id: string): Observable<string> {
    return this.http
      .get<{ ingredientCsvTemplate: string }>(this.URL + `/${id}/download`)
      .pipe(
        map((res) => res.ingredientCsvTemplate),
        catchError(this.errorService.handleError),
      )
  }
}
